import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormsModule } from '@angular/forms';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IRawShortcut, IShortcutFilter, ShortcutService, SHORTCUT_LOC_DISPLAY } from 'src/app/services/shortcut.service';
import { object, pick, pluck, values } from 'underscore';
import { cloneDeep } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { of, Subject } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { CATEGORY_ENUM, DATE_OPTION_ENUM } from '../interfaces';
import { AnalyticsService } from 'src/app/services/analytics.service';

import { ShortcutEditModalComponent } from 'src/app/pages/settings/shortcuts/shortcut-settings/shortcut-edit-modal/shortcut-edit-modal.component';
import { SPINNER_BUTTON_STATUS_ENUM, SpinnerButtonService } from '@components/spinner-button/spinner-button.service';
import { UIStreamlineIconComponent } from '@ui/streamline-icon/ui-streamline-icon.component';
import { WithDestroy } from 'src/app/shared/utils/mixins/withDestroy';
import { toSignal } from '@angular/core/rxjs-interop';
import { CategorySelectorService } from '../category-selector/category-selector.service';
import { USER_STATUS_TO_CLASSIFIED } from 'src/app/services/user.service';


@Component({
    selector: 'app-shortcut-button-new',
    imports: [CommonModule, FormsModule, UIStreamlineIconComponent],
    templateUrl: './shortcut-button-new.component.html',
    styleUrls: ['./shortcut-button-new.component.scss']
})
export class ShortcutButtonNewComponent extends WithDestroy() {
  @Input() activeShortcut = null;
  @Input() isFilterChangedAndRan = false;
  @Input() controls: { [key: string]: AbstractControl };
  @Input() disabled = false;
  @Input() dateOption = DATE_OPTION_ENUM.thisMonth;
  @Output() isFilterChangedAndRanChange = new EventEmitter();
  @Output() discard = new EventEmitter();

  newTitle: string;
  existingTitle: string;
  isSubmitting = false;
  activeDropdown = 0; // to decide which dropdown is shown.

  isActive = false;

  // for duplicate name error
  isNameDuplicate = false;

  destroy$ = new Subject();

  @ViewChild('myDrop') dropdown: NgbDropdown;

  shortcuts$ = this.shortcutService.getShortcuts();

  @HostListener('document:click', ['$event'])
  clickout(event) {
    this.dropdown?.close();
  }

  firstResultFetched = false;

  spinnerStatus$ = this.spinnerButtonService.statusUpdate$.pipe(
    filter(s => s !== undefined),
    tap(s => {
      if (s === SPINNER_BUTTON_STATUS_ENUM.after) {
        this.firstResultFetched = true;
      }
    })
  );
  SPINNER_BUTTON_STATUS_ENUM = SPINNER_BUTTON_STATUS_ENUM;
  categoryOptions = toSignal(inject(CategorySelectorService).getSelectorOptions());

  constructor(
    private shortcutService: ShortcutService,
    private toaster: ToastrService,
    private analytics: AnalyticsService,
    private modalService: NgbModal,
    private cdr: ChangeDetectorRef,
    private spinnerButtonService: SpinnerButtonService,

  ) {
    super();
    // keep updating default title
    this.suggestedTitle().subscribe(t => this.newTitle = t);
  }



  ngOnChanges(changes: SimpleChanges): void {
    this.existingTitle = this.activeShortcut?.title;
    // if (changes.spinnerButtonStatus?.currentValue === SPINNER_BUTTON_STATUS_ENUM.reset) {
    //   this.shortcutService.setActiveShortcut(null)
    // }
  }

  openSaveModal() {
    if (this.activeShortcut) {
      this.activeShortcut = null;
      return;
    }
    const modal = this.modalService.open(ShortcutEditModalComponent, {size: 'lg'});
    modal.componentInstance.shortcut = {
      shortcutID: null,
      filters: this.getFilterValues(),
      lastOpened: Date.now(),
      title: this.newTitle
    }
    modal.result.then(res => {
      if (res) {
        // this.dropdown.close();
        this.toaster.success(`Filter successfully saved`);
        this.activeShortcut = res;
        this.sendTracking(res, true);
        this.isFilterChangedAndRan = false;
        this.isFilterChangedAndRanChange.emit(false);
        this.cdr.markForCheck();
      }
    })
  }

  checkNameValid(isSavingNew) {
    if (!isSavingNew) {
      return of(true);
    }
    return this.shortcutService.getShortcuts()
      .pipe(take(1), map(shortcuts => !pluck(shortcuts, 'title').includes(this.newTitle)));
  }

  loadFilter() {
    this.controls.userStatus.setValue(this.activeShortcut.filters.userStatus);
  }

  ngOnDestroy(): void {
    this.destroy$.next();

  }

  getFilterValues(): IShortcutFilter {
    const formValues = object(Object.entries(this.controls)
      .map(([k, v]) => {
        return [k, v.value];
      })
    ) as IShortcutFilter;
    // return {...formValues, items: formValues.items.concat('efd2f530-74c8-4481-b3fc-238a74a59641')};
    return {
      ...cloneDeep(formValues),
      dates: {
        dateOption: this.dateOption
      },
      isCategoryAllSelected: this.isAllCategorySelected(formValues)

    };
  }

  // checks if all options are selected in the category type field
  // eg if all users are selected
  isAllCategorySelected(filters: IShortcutFilter) {
    if (filters.filterBy === CATEGORY_ENUM.individual) {
      return values(pick(this.categoryOptions().users, 
        filters.userStatus.map(s => USER_STATUS_TO_CLASSIFIED[s])))?.flat()?.length === filters.items?.length
    }
    return filters.items.length === (this.categoryOptions()[filters.filterBy] as any[])?.length
  }


  get showDropdown() {
    return this.activeShortcut && this.isFilterChangedAndRan;
  }

  onDropdownChange(event) {
    if (!event && this.activeDropdown !== 0) {
      this.activeDropdown = 0;
    }
  }

  openModal(modal) {
    this.activeDropdown = modal;
    this.dropdown.open();
  }

  onType() {
    this.isNameDuplicate = false;
  }

  suggestedTitle() {
    const title = SHORTCUT_LOC_DISPLAY[this.shortcutService.getRawLocationOnUrl()];
    return this.shortcutService.getShortcuts().pipe(
      takeUntil(this.destroy$),
      map(shortcuts => pluck(shortcuts, 'title')),
      map(shortcutTitles => {
        let i = 1;
        while (shortcutTitles.includes(title + ' ' + i)) {
          i += 1;
        }
        return title + ' ' + i;
      })
    );
  }

  discardChanges() {
    // this.shortcutService.setActiveShortcut(this.activeShortcut.shortcutID);
    this.discard.emit();
    setTimeout(() => {
      this.dropdown.close();
    }, 0);
    // this.cdr.detectChanges();
  }

  sendTracking(res: IRawShortcut, isSavingNew) {

    this.analytics.track('S-savedFilter-saved', {
      type: isSavingNew ? 'new' : 'existing',
      ...this.shortcutService.getEventBody(res.shortcutID)
    });
  }

  get slIcon() {
    return this.activeShortcut 
    ? {name: 'interface-bookmark', theme: 'micro-bold'}
    : { name: 'bookmark', theme: 'core-line' }
  }

  get btnColour() {
    return this.activeShortcut
    ? 'pink'
    : 'neutral'
  }



}
